import {
    BaseHook,
    ClientMetadata,
    ClientProviderEvents,
    ClientProviderStatus,
    EvaluationDetails,
    EventHandler,
    FlagValue,
    JsonValue,
    Logger,
    TrackingEventDetails,
} from '@openfeature/core'
import { Client, FlagEvaluationOptions } from '@openfeature/react-sdk'

/**
 * Caches repeated getFlag()/getStringFlag() lookups in performance-sensitive list and render paths.
 * OpenFeature runs its evaluation hooks for each uncached lookup. Flagsmith remains the source of
 * truth, so changed flags and targeting context changes remove entries from this cache.
 * Remove this adapter once callers no longer repeat flag lookups in performance-sensitive paths.
 */
export class CachedOpenFeatureClient implements Client {
    constructor(private readonly baseClient: Client) {
        baseClient.addHandler(ClientProviderEvents.ConfigurationChanged, details => {
            if (!details?.flagsChanged) {
                this.cache.clear()
                return
            }

            details.flagsChanged.forEach(flagKey => this.cache.delete(flagKey))
        })
        baseClient.addHandler(ClientProviderEvents.ContextChanged, () => this.cache.clear())
    }

    private readonly cache = new Map<string, EvaluationDetails<FlagValue>>()

    private cached<T extends FlagValue>(flagKey: string, getValue: () => EvaluationDetails<T>): EvaluationDetails<T> {
        if (this.cache.has(flagKey)) {
            return this.cache.get(flagKey) as EvaluationDetails<T>
        }

        const details = getValue()
        details.reason = 'CACHED'
        this.cache.set(flagKey, details)
        return details
    }

    get metadata(): ClientMetadata {
        return this.baseClient.metadata
    }

    get providerStatus(): ClientProviderStatus {
        return this.baseClient.providerStatus
    }

    addHooks(...hooks: BaseHook[]): Client {
        this.baseClient.addHooks(...hooks)
        return this
    }

    getHooks(): BaseHook[] {
        return this.baseClient.getHooks()
    }

    clearHooks(): Client {
        this.baseClient.clearHooks()
        return this
    }

    getBooleanValue(flagKey: string, defaultValue: boolean, options?: FlagEvaluationOptions): boolean {
        return this.getBooleanDetails(flagKey, defaultValue, options).value
    }

    getBooleanDetails(flagKey: string, defaultValue: boolean, options?: FlagEvaluationOptions): EvaluationDetails<boolean> {
        return this.cached(flagKey, () => this.baseClient.getBooleanDetails(flagKey, defaultValue, options))
    }

    getStringValue(flagKey: string, defaultValue: string, options?: FlagEvaluationOptions): string {
        return this.getStringDetails(flagKey, defaultValue, options).value
    }

    getStringDetails(flagKey: string, defaultValue: string, options?: FlagEvaluationOptions): EvaluationDetails<string> {
        return this.cached(flagKey, () => this.baseClient.getStringDetails(flagKey, defaultValue, options))
    }

    getNumberValue(flagKey: string, defaultValue: number, options?: FlagEvaluationOptions): number {
        return this.getNumberDetails(flagKey, defaultValue, options).value
    }

    getNumberDetails(flagKey: string, defaultValue: number, options?: FlagEvaluationOptions): EvaluationDetails<number> {
        return this.cached(flagKey, () => this.baseClient.getNumberDetails(flagKey, defaultValue, options))
    }

    getObjectValue(flagKey: string, defaultValue: JsonValue, options?: FlagEvaluationOptions): JsonValue {
        return this.getObjectDetails(flagKey, defaultValue, options).value
    }

    getObjectDetails(flagKey: string, defaultValue: JsonValue, options?: FlagEvaluationOptions): EvaluationDetails<JsonValue> {
        return this.cached(flagKey, () => this.baseClient.getObjectDetails(flagKey, defaultValue, options))
    }

    setLogger(logger: Logger): Client {
        this.baseClient.setLogger(logger)
        return this
    }

    addHandler(eventType: ClientProviderEvents, handler: EventHandler<ClientProviderEvents>): void {
        this.baseClient.addHandler(eventType, handler)
    }

    removeHandler(eventType: ClientProviderEvents, handler: EventHandler<ClientProviderEvents>): void {
        this.baseClient.removeHandler(eventType, handler)
    }

    getHandlers(eventType: ClientProviderEvents): EventHandler<ClientProviderEvents>[] {
        return this.baseClient.getHandlers(eventType)
    }

    track(trackingEventName: string, trackingEventDetails?: TrackingEventDetails): void {
        this.baseClient.track(trackingEventName, trackingEventDetails)
    }
}
